import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { UIState, Notification, ToastNotification } from '@/types';

// Initial state
const initialState: UIState = {
  sidebarOpen: true,
  loading: false,
  notifications: [],
  toasts: [],
  currentPage: 'dashboard',
};

// UI slice
export const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },

    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload;
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    setCurrentPage: (state, action: PayloadAction<string>) => {
      state.currentPage = action.payload;
    },

    addNotification: (state, action: PayloadAction<Omit<Notification, 'id' | 'timestamp' | 'read'>>) => {
      const notification: Notification = {
        ...action.payload,
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        timestamp: new Date().toISOString(),
        read: false,
      };
      state.notifications.unshift(notification);
      
      // Keep only the last 50 notifications
      if (state.notifications.length > 50) {
        state.notifications = state.notifications.slice(0, 50);
      }
    },

    markNotificationAsRead: (state, action: PayloadAction<string>) => {
      const notification = state.notifications.find(n => n.id === action.payload);
      if (notification) {
        notification.read = true;
      }
    },

    markAllNotificationsAsRead: (state) => {
      state.notifications.forEach(notification => {
        notification.read = true;
      });
    },

    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(n => n.id !== action.payload);
    },

    clearNotifications: (state) => {
      state.notifications = [];
    },

    // Quick notification helpers
    showSuccess: (state, action: PayloadAction<{ title: string; message: string }>) => {
      const notification: Notification = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        type: 'success',
        title: action.payload.title,
        message: action.payload.message,
        timestamp: new Date().toISOString(),
        read: false,
      };
      state.notifications.unshift(notification);
      
      if (state.notifications.length > 50) {
        state.notifications = state.notifications.slice(0, 50);
      }
    },

    showError: (state, action: PayloadAction<{ title: string; message: string }>) => {
      const notification: Notification = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        type: 'error',
        title: action.payload.title,
        message: action.payload.message,
        timestamp: new Date().toISOString(),
        read: false,
      };
      state.notifications.unshift(notification);
      
      if (state.notifications.length > 50) {
        state.notifications = state.notifications.slice(0, 50);
      }
    },

    showWarning: (state, action: PayloadAction<{ title: string; message: string }>) => {
      const notification: Notification = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        type: 'warning',
        title: action.payload.title,
        message: action.payload.message,
        timestamp: new Date().toISOString(),
        read: false,
      };
      state.notifications.unshift(notification);
      
      if (state.notifications.length > 50) {
        state.notifications = state.notifications.slice(0, 50);
      }
    },

    showInfo: (state, action: PayloadAction<{ title: string; message: string }>) => {
      const notification: Notification = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        type: 'info',
        title: action.payload.title,
        message: action.payload.message,
        timestamp: new Date().toISOString(),
        read: false,
      };
      state.notifications.unshift(notification);
      
      if (state.notifications.length > 50) {
        state.notifications = state.notifications.slice(0, 50);
      }
    },

    // Toast notification actions
    showToast: (state, action: PayloadAction<Omit<ToastNotification, 'id' | 'timestamp'>>) => {
      const toast: ToastNotification = {
        ...action.payload,
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        timestamp: new Date().toISOString(),
      };
      state.toasts.push(toast);
      
      // Keep only the last 10 toasts in queue
      if (state.toasts.length > 10) {
        state.toasts = state.toasts.slice(-10);
      }
    },

    hideToast: (state, action: PayloadAction<string>) => {
      state.toasts = state.toasts.filter(toast => toast.id !== action.payload);
    },

    clearToasts: (state) => {
      state.toasts = [];
    },

    // Quick toast helpers
    showToastSuccess: (state, action: PayloadAction<{ title: string; message: string; duration?: number }>) => {
      const toast: ToastNotification = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        type: 'success',
        title: action.payload.title,
        message: action.payload.message,
        duration: action.payload.duration,
        timestamp: new Date().toISOString(),
      };
      state.toasts.push(toast);
      
      if (state.toasts.length > 10) {
        state.toasts = state.toasts.slice(-10);
      }
    },

    showToastError: (state, action: PayloadAction<{ title: string; message: string; duration?: number }>) => {
      const toast: ToastNotification = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        type: 'error',
        title: action.payload.title,
        message: action.payload.message,
        duration: action.payload.duration,
        timestamp: new Date().toISOString(),
      };
      state.toasts.push(toast);
      
      if (state.toasts.length > 10) {
        state.toasts = state.toasts.slice(-10);
      }
    },

    showToastWarning: (state, action: PayloadAction<{ title: string; message: string; duration?: number }>) => {
      const toast: ToastNotification = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        type: 'warning',
        title: action.payload.title,
        message: action.payload.message,
        duration: action.payload.duration,
        timestamp: new Date().toISOString(),
      };
      state.toasts.push(toast);
      
      if (state.toasts.length > 10) {
        state.toasts = state.toasts.slice(-10);
      }
    },

    showToastInfo: (state, action: PayloadAction<{ title: string; message: string; duration?: number }>) => {
      const toast: ToastNotification = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        type: 'info',
        title: action.payload.title,
        message: action.payload.message,
        duration: action.payload.duration,
        timestamp: new Date().toISOString(),
      };
      state.toasts.push(toast);
      
      if (state.toasts.length > 10) {
        state.toasts = state.toasts.slice(-10);
      }
    },
  },
});

export const {
  toggleSidebar,
  setSidebarOpen,
  setLoading,
  setCurrentPage,
  addNotification,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  removeNotification,
  clearNotifications,
  showSuccess,
  showError,
  showWarning,
  showInfo,
  showToast,
  hideToast,
  clearToasts,
  showToastSuccess,
  showToastError,
  showToastWarning,
  showToastInfo,
} = uiSlice.actions;

// Selectors
export const selectUI = (state: { ui: UIState }) => state.ui;
export const selectSidebarOpen = (state: { ui: UIState }) => state.ui.sidebarOpen;
export const selectLoading = (state: { ui: UIState }) => state.ui.loading;
export const selectCurrentPage = (state: { ui: UIState }) => state.ui.currentPage;
export const selectNotifications = (state: { ui: UIState }) => state.ui.notifications;
export const selectUnreadNotifications = (state: { ui: UIState }) =>
  state.ui.notifications.filter(n => !n.read);
export const selectUnreadNotificationsCount = (state: { ui: UIState }) =>
  state.ui.notifications.filter(n => !n.read).length;
export const selectToasts = (state: { ui: UIState }) => state.ui.toasts;